import { LoadMoreResults } from '@websrc/App/Components/LoadMoreResults'
import { useStaticText } from '@websrc/App/Hooks/useStaticText'
import { mapSelector } from '@websrc/DataMappers/mapSelector'
import clsx from 'clsx'
import { gridArea, gridAreaTemplate, styles, typography } from 'leds'
import React, { useState } from 'react'

import { SelectorListItem } from './SelectorListItem'

interface SelectorListProps {
  items: ReturnType<typeof mapSelector>['selector_items']
  nrOfItemsPerLoad: number
  className?: string
}

export const SelectorList: React.FC<SelectorListProps> = ({
  items,
  nrOfItemsPerLoad,
  className,
}) => {
  const t = useStaticText()
  const [nrOfShownItems, updateNrOfShownItem] = useState(Math.min(nrOfItemsPerLoad, items.length))

  const shouldShowImageColumn = items.some((item) => Boolean(item.image))
  const shouldShowGradeBand = items.some((item) => Boolean(item.icon.label))

  const getItemsThatNeedToBeShown = (
    allItems: SelectorListProps['items'],
    nrToBeShown: number,
    mapItem: (item: SelectorListProps['items'][0], index: number) => React.JSX.Element
  ) => {
    const shownItems = []

    for (let index = 0; index < Math.min(nrToBeShown, allItems.length); index++) {
      const item = allItems[index]

      shownItems[index] = mapItem(item, index)
    }

    return shownItems
  }

  const mapListItem = (item: SelectorListProps['items'][0], index: number) => {
    return (
      <SelectorListItem
        content={item}
        dataTestId={`list-selector-item-${index}`}
        key={index}
        showGradeBand={shouldShowGradeBand}
        showImageColumn={shouldShowImageColumn}
      />
    )
  }

  return (
    <>
      <div
        className={clsx(
          gridAreaTemplate({
            template: shouldShowGradeBand ? 'listSelectorHeader' : 'listSelectorHeaderNoGradeband',
          }),
          styles({
            paddingX: 16,
            paddingBottom: 8,
            width: 'full',
          }),
          className
        )}
        data-testid={`list-${shouldShowImageColumn}-${shouldShowGradeBand}`}
      >
        <div className={gridArea({ area: 'name' })}>
          <p className={typography({ size: 'md' })}>{t.global.name}</p>
        </div>
        {shouldShowGradeBand && (
          <div className={gridArea({ area: 'grade' })}>
            <p className={typography({ size: 'md' })}>{t.plans_lessons_worksheets.grade_band}</p>
          </div>
        )}
      </div>

      <div>
        {getItemsThatNeedToBeShown(items, nrOfShownItems, mapListItem)}
        <LoadMoreResults
          loadMoreFn={() => {
            updateNrOfShownItem(Math.min(nrOfShownItems + nrOfItemsPerLoad, items.length))
          }}
          numberHits={nrOfShownItems}
          resultsInfoStaticText={t.plans_lessons_worksheets.showing_entries}
          totalHits={items.length}
        >
          {t.plans_lessons_worksheets.load_more}
        </LoadMoreResults>
      </div>
    </>
  )
}
